import React from 'react'
import Chart from 'chart.js'
import { pathOr, length } from 'ramda'
import { isNotNil } from '../../../../helpers'
import { StyledChartLegendWrapper, StyledChartLegend, StyledChartLegendColor, StyledChartLegendName } from './styled'

Chart.defaults.global.responsive = true
Chart.defaults.global.maintainAspectRatio = false
Chart.defaults.global.legend.display = false
Chart.defaults.global.elements.point.radius = 0
Chart.defaults.global.elements.point.hoverRadius = 0
Chart.defaults.global.tooltips.enabled = false
Chart.defaults.global.tooltips.mode = 'nearest'
Chart.defaults.global.tooltips.intersect = true
Chart.defaults.global.tooltips.backgroundColor = "#ffffff"
Chart.defaults.global.tooltips.custom = function (tooltipModel) {
    var show = false;
    var data = {};
    var dataPoints = pathOr([], ['dataPoints'], tooltipModel);
    if (length(dataPoints) === 1) {
        var index = pathOr(null, ['index'], dataPoints[0]);
        var datasetIndex = pathOr(null, ['datasetIndex'], dataPoints[0]);
        var datasets = pathOr([], ['_chart', 'config', 'data', 'datasets'], this);
        show = pathOr(false, ['tooltip', 'enabled'], datasets[datasetIndex]);
        data = datasets[datasetIndex]['data'][index];
    } else {
        show = false;
    }
    // Tooltip Element
    var tooltipEl = document.getElementById('chartjs-tooltip');

    // Create element on first render
    if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.id = 'chartjs-tooltip';
        document.body.appendChild(tooltipEl);
    }

    // Hide if no tooltip
    if (tooltipModel.opacity === 0 || !show) {
        tooltipEl.style.opacity = 0;
        return;
    }

    // Set caret Position
    tooltipEl.classList.remove('above', 'below', 'no-transform');
    if (tooltipModel.yAlign) {
        tooltipEl.classList.add(tooltipModel.yAlign);
    } else {
        tooltipEl.classList.add('no-transform');
    }

    // Set Copy
    if (data.copy) {
        tooltipEl.innerHTML = "";
        if (data.copy.header) {
            var header = document.createElement('div');
            header.innerHTML = data.copy.header;
            Object.assign(header.style, {
                fontFamily: tooltipModel._titleFontFamily ? tooltipModel._titleFontFamily : 'Arial',
                fontSize: tooltipModel.titleFontSize ? `${tooltipModel.titleFontSize}px` : '12px',
                fontWeight: tooltipModel._titleFontStyle ? tooltipModel._titleFontStyle : 'bold',
                color: tooltipModel.titleFontColor ? tooltipModel.titleFontColor : "#000000",
                textAlign: tooltipModel._titleAlign ? tooltipModel._titleAlign : `left`,
                marginBottom: tooltipModel.titleMarginBottom ? `${tooltipModel.titleMarginBottom}px` : `0px`
            })
            tooltipEl.appendChild(header);
        }
        if (data.copy.body) {
            var body = document.createElement('div');
            body.innerHTML = data.copy.body;
            Object.assign(body.style, {
                fontFamily: tooltipModel._bodyFontFamily ? tooltipModel._bodyFontFamily : 'Arial',
                fontSize: tooltipModel.bodyFontSize ? `${tooltipModel.bodyFontSize}px` : '12px',
                fontWeight: tooltipModel._bodyFontStyle ? tooltipModel._bodyFontStyle : 'normal',
                color: tooltipModel.bodyFontColor ? tooltipModel.bodyFontColor : "#000000",
                textAlign: tooltipModel._bodyAlign ? tooltipModel._bodyAlign : `left`
            })
            tooltipEl.appendChild(body);
        }
    }

    // `this` will be the overall tooltip
    var position = this._chart.canvas.getBoundingClientRect();

    // Display, position, and set styles for font
    Object.assign(tooltipEl.style, {
        opacity: 1,
        position: 'absolute',
        left: `${position.left + window.pageXOffset + tooltipModel.caretX + 5}px`,
        top: `${position.top + window.pageYOffset + tooltipModel.caretY + 5}px`,
        fontFamily: tooltipModel._bodyFontFamily,
        fontSize: `${tooltipModel.bodyFontSize}px`,
        fontStyle: tooltipModel._bodyFontStyle,
        padding: `${tooltipModel.yPadding}px ${tooltipModel.xPadding}px`,
        pointerEvents: 'none',
        background: tooltipModel.backgroundColor,
        borderRadius: `${tooltipModel.cornerRadius}px`,
        borderWidth: `${tooltipModel.borderWidth}px`,
        borderColor: `${tooltipModel.borderColor}px`,
        boxShadow: `5px 5px 15px -1px rgba(0,0,0,0.55)`
    })
}
Chart.defaults.global.onClick = (point, event) => {
    if (event[0]) {
        const datasets = event[0]._chart.config.data.datasets;
        const datasetIndex = event[0]._datasetIndex;
        const index = event[0]._index;
        if (datasets) {
            const isNotesData = pathOr(false, [datasetIndex, 'notes'], datasets);
            const noteIndex = pathOr(null, [datasetIndex, 'data', index, ['index']], datasets);
            if (isNotesData && isNotNil(noteIndex)) {
                const elNotes = document.querySelectorAll(".report-table-note");
                const elNote = document.getElementById(`note-${noteIndex}`);
                if(elNotes && elNote) {
                    window.scrollTo({
                        top: elNote.getBoundingClientRect().top + window.pageYOffset - 65,
                        behavior: 'smooth'
                    });
                    elNotes.forEach(el => el.style.background = "none");
                    elNote.style.backgroundColor = elNote.dataset.background;
                }
            }
        }
    }
}

Chart.defaults.global.legendCallback = (chart) => {
    const { data } = chart
    const legend = data && data.datasets.reduce((prev, curr) => {
        if (prev.find(a => a.legendName === curr.legendName)) {
            return prev
        } else {
            return [
                ...prev,
                curr
            ]
        }
    }, [])
    return (
        <StyledChartLegendWrapper>
            {
                legend && legend.map((item, index) => {
                    return (
                        <StyledChartLegend key={index}>
                            <StyledChartLegendColor legendColor={item.legendColor} legendType={item.legendType} />
                            <StyledChartLegendName>{item.legendName}</StyledChartLegendName>
                        </StyledChartLegend>
                    )
                })
            }
        </StyledChartLegendWrapper>
    )
}

Chart.plugins.register({
    afterDatasetsDraw: function (chart, easing) {
        var ctx = chart.ctx;
        chart.data.datasets.forEach(function (dataset, i) {
            var meta = chart.getDatasetMeta(i);
            if (meta.type === "bubble") { //exclude scatter
                meta.data.forEach(function (element, index) {
                    // Draw the text in black, with the specified font
                    ctx.fillStyle = 'rgb(255, 255, 255)';
                    var fontSize = 12;
                    var fontStyle = 'bold';
                    var fontFamily = 'Lato';
                    ctx.font = Chart.helpers.fontString(fontSize, fontStyle, fontFamily);

                    // Just naively convert to string for now
                    var dataString = dataset.data[index].number ? dataset.data[index].number : '';
                    // Make sure alignment settings are correct
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';

                    var padding = 7;
                    var position = element.tooltipPosition();
                    ctx.fillText(dataString, position.x, position.y - (fontSize / 2) + padding);
                });
            }
        });
    }
})

export default Chart